import React from "react"
import styled, {keyframes} from "styled-components"
import Layout from "../Layout";
import {Center, PageTitle} from "../shared/mix";
import SEO from "../SEO";

const skills = [
    "JavaScript", "TypeScript", "React", "Next.js", "Remix / React Router", "Node.js", "Express.js",
    "Redux", "TanStack Query", "GraphQL", "REST API", "gRPC", "Jest",
    "Svelte", "Astro", "Angular",
    "PHP", "Laravel", "Nette", "Yii2", "WordPress", "PocketBase",
    "MySQL", "PostgreSQL",
    "Tailwind CSS", "Styled Components", "CSS Modules", "Sass", "CSS",
    "Docker", "CI/CD", "Git", "Linux", "DNS", "Sítě",
    "Figma", "Adobe Illustrator", "Photoshop",
    "C#",
]

const CV = () => {
    return (
        <Layout>
            <SEO
                title="CV — Životopis"
                breadcrumb="CV"
                pageType="ProfilePage"
                ogType="profile"
                image="https://puhony.eu/img/profile.jpg"
                description="Životopis Matěje Půhoného — frontend a full-stack web developer z Pardubic. React, TypeScript, Node.js, Next.js, PHP/Laravel. Frontend developer v nextap solutions (Steller.co, Clevernajem), freelancer od 2019."
                jsonLd={{
                    "@type": "ProfilePage",
                    "@id": "https://puhony.eu/cv/#webpage",
                    "dateModified": "2026-10-18",
                    "mainEntity": {
                        "@id": "https://puhony.eu/#person",
                        "@type": "Person",
                        "name": "Matěj Půhoný",
                        "honorificPrefix": "Bc.",
                        "jobTitle": "Frontend & full-stack web developer",
                        "email": "info@puhony.eu",
                        "url": "https://puhony.eu/cv/",
                        "image": "https://puhony.eu/img/profile.jpg",
                        "sameAs": ["https://www.linkedin.com/in/matej-puhony/"],
                        "worksFor": {"@type": "Organization", "name": "nextap solutions", "url": "https://nextap.eu/"},
                        "knowsAbout": skills,
                        "alumniOf": [
                            {"@type": "CollegeOrUniversity", "name": "Univerzita Hradec Králové — Fakulta informatiky a managementu"},
                            {"@type": "CollegeOrUniversity", "name": "České vysoké učení technické v Praze"},
                            {"@type": "CollegeOrUniversity", "name": "VIVES University of Applied Sciences, Belgie"},
                            {"@type": "HighSchool", "name": "DELTA — Střední škola informatiky a ekonomie, Pardubice"}
                        ],
                        "hasCredential": {"@type": "EducationalOccupationalCredential", "name": "Networking Essentials"},
                        "award": [
                            "Pardubický talent 2022",
                            "Ocenění Pardubického kraje — BurzaŠkol",
                            "2. místo — Ústřední kolo programování web 2021",
                            "1. místo — Krajské kolo programování web 2021",
                            "Mladý řemeslník roku 2019"
                        ]
                    }
                }}
            />
            <CvCenter>
                <Header>
                    <Avatar />
                    <HeaderInfo>
                        <Name>Bc. Matěj Půhoný</Name>
                        <Role>Frontend & full-stack web developer</Role>
                        <HeaderMeta>
                            <MetaItem>
                                <i className="fa-solid fa-location-dot" />
                                Pardubice, Hradec Králové, Česká republika
                            </MetaItem>
                            <MetaItem>
                                <i className="fa-solid fa-at" />
                                info@puhony.eu
                            </MetaItem>
                            <MetaItem>
                                <i className="fa-brands fa-linkedin" />
                                <MetaLink href="https://www.linkedin.com/in/matej-puhony/" target="_blank" rel="noopener noreferrer">linkedin.com/in/matej-puhony</MetaLink>
                            </MetaItem>
                        </HeaderMeta>
                    </HeaderInfo>
                </Header>

                <Section style={{animationDelay: '0.05s'}}>
                    <Summary>
                        Webový vývojář z Pardubic a Hradce Králové. Denně pracuji s Reactem, TypeScriptem a dalším JS/TS ekosystémem,
                        rád zkouším nové technologie, automatizuji procesy a vedle hlavní práce dělám menší freelance zakázky.
                        Baví mě i PHP a backend. Když to čas dovolí, mentoruji středoškoláky při maturitních projektech a na hackathonech.
                        Aktuálně jsem otevřený novým příležitostem.
                    </Summary>
                </Section>

                <Section style={{animationDelay: '0.1s'}}>
                    <SectionLabel>Technologie & skills</SectionLabel>
                    <TagCloud>
                        {skills.map((skill, i) => (
                            <SkillTag key={i}>{skill}</SkillTag>
                        ))}
                    </TagCloud>
                </Section>

                <Section style={{animationDelay: '0.15s'}}>
                    <SectionLabel>Pracovní zkušenosti</SectionLabel>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>nextap solutions</ItemTitle>
                                <ItemDate>06/2020 — současnost</ItemDate>
                            </ItemHeader>
                            <ItemRole>Frontend developer · Pardubice</ItemRole>

                            <SubRoleList>
                                <SubRole>
                                    <ItemHeader>
                                        <SubRoleTitle>Frontend developer — Steller.co</SubRoleTitle>
                                        <ItemDate>08/2026 — současnost</ItemDate>
                                    </ItemHeader>
                                    <ItemDesc>
                                        Návrat ke Steller.co — aplikaci pro plánování cest a sdílení cestovatelských příběhů pomocí foto a video stories.
                                    </ItemDesc>
                                    <TechLine>React · TypeScript · Node.js · GraphQL · Server-Sent Events · CSS-in-JS · CSS Modules · Git</TechLine>
                                </SubRole>

                                <SubRole>
                                    <ItemHeader>
                                        <SubRoleTitle>Project tech lead — Clevernajem (property management system)</SubRoleTitle>
                                        <ItemDate>10/2024 — současnost</ItemDate>
                                    </ItemHeader>
                                    <ItemDesc>
                                        Vedení vývoje interního systému (ERP / back-office) pro správu nemovitostí. Platforma automatizuje měsíční
                                        výplaty majitelům, výběr nájmů a sledování plateb, správu nájemníků a nemovitostí a další každodenní agendu.
                                    </ItemDesc>
                                    <TechLine>React · TypeScript · Remix / React Router · Node.js · PocketBase · REST API · Tailwind CSS · Docker · CI/CD · bankovní API</TechLine>
                                </SubRole>

                                <SubRole>
                                    <ItemHeader>
                                        <SubRoleTitle>Frontend developer — interní projekty</SubRoleTitle>
                                        <ItemDate>06/2020 — současnost</ItemDate>
                                    </ItemHeader>
                                    <ItemDesc>
                                        Vývoj interních produktů a webových aplikací pro české i zahraniční klienty — responzivní UI podle návrhů
                                        z Figmy, integrace API a optimalizace výkonu.
                                    </ItemDesc>
                                    <TechLine>React · Next.js · TypeScript · Svelte · Astro · Angular · PocketBase · REST · gRPC · Tailwind CSS</TechLine>
                                </SubRole>

                                <SubRole>
                                    <ItemHeader>
                                        <SubRoleTitle>Web frontend developer — Steller.co</SubRoleTitle>
                                        <ItemDate>01/2021 — 10/2024</ItemDate>
                                    </ItemHeader>
                                    <ItemDesc>
                                        Téměř čtyři roky vývoje webové aplikace Steller.co v průběhu celé její evoluce:
                                    </ItemDesc>
                                    <BulletList>
                                        <li>interaktivní mapy pro výlety a lokace</li>
                                        <li>přepracování video přehrávače a media feedů</li>
                                        <li>kompletní redesign aplikace a komponenty design systému</li>
                                        <li>zlepšení výkonu webu</li>
                                        <li>vývoj funkce plánování cest</li>
                                    </BulletList>
                                    <TechLine>React · Redux · TypeScript · GraphQL · Node.js · Styled Components · Google Maps</TechLine>
                                </SubRole>

                                <SubRole>
                                    <ItemHeader>
                                        <SubRoleTitle>Junior web developer</SubRoleTitle>
                                        <ItemDate>06/2020 — 01/2021</ItemDate>
                                    </ItemHeader>
                                    <ItemDesc>
                                        Práce na několika projektech, především Foosapp — webové aplikaci pro sledování zápasů, statistik hráčů
                                        a žebříčků pro komunitu stolního fotbálku. Dále správa firemního webu a práce na klientských projektech.
                                    </ItemDesc>
                                    <TechLine>React · Redux · TypeScript · Tailwind CSS · Node.js · Express.js · PHP · WordPress · REST · gRPC · WebSockets</TechLine>
                                </SubRole>
                            </SubRoleList>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Freelance developer (OSVČ)</ItemTitle>
                                <ItemDate>06/2019 — současnost</ItemDate>
                            </ItemHeader>
                            <ItemRole>Web developer & designer · Královéhradecký kraj</ItemRole>
                            <ItemDesc>
                                Weby, webové aplikace a firemní systémy na míru pro firmy i jednotlivce — od analýzy požadavků přes UI/UX návrh,
                                vývoj a nasazení až po hosting, nastavení DNS a dlouhodobou údržbu.
                            </ItemDesc>
                            <BulletList>
                                <li>lasergame-hk.cz — online rezervační systém a interní administrace</li>
                                <li>e-voucher.cz — multi-tenant platforma pro voucherové e-shopy (e-commerce, platební brána)</li>
                                <li>zapisdoprvnitridy.cz — webová aplikace pro zápis do školy</li>
                                <li>skautstezery.cz — web skautského oddílu</li>
                                <li>a další</li>
                            </BulletList>
                            <TechLine>React · Next.js · TypeScript · Node.js · PHP · Laravel · Nette · MySQL · PostgreSQL · WordPress · Figma · Linux · Docker · CI/CD</TechLine>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>DAMI development s.r.o.</ItemTitle>
                                <ItemDate>03/2019 — 03/2020</ItemDate>
                            </ItemHeader>
                            <ItemRole>Junior frontend developer · Hradec Králové</ItemRole>
                            <ItemDesc>
                                Junior PHP a React developer na klientských webových aplikacích a webech — od backendové logiky po frontend.
                            </ItemDesc>
                            <BulletList>
                                <li>tanix.cz — dashboard pro monitoring IoT zařízení</li>
                                <li>crowningarts.com — sociální síť pro umělce</li>
                                <li>akkn.cz — klientský web</li>
                            </BulletList>
                            <TechLine>PHP · Yii2 · React · JavaScript · MySQL · HTML · CSS · WordPress</TechLine>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Paiwand — Afghan association London</ItemTitle>
                                <ItemDate>2019</ItemDate>
                            </ItemHeader>
                            <ItemRole>Stáž — Erasmus</ItemRole>
                            <ItemDesc>Práce na interním systému v rámci programu Erasmus.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>
                </Section>

                <Section style={{animationDelay: '0.2s'}}>
                    <SectionLabel>Vybrané projekty</SectionLabel>
                    <ProjectGrid>
                        <ProjectItem>
                            <ProjectName>Steller.co</ProjectName>
                            <ProjectSub>Plánování cest a foto/video příběhy pro cestovatele</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Clevernajem</ProjectName>
                            <ProjectSub>Interní systém pro správu nemovitostí</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Foosapp.eu</ProjectName>
                            <ProjectSub>Aplikace pro stolní fotbálek</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>LaserGame-hk.cz</ProjectName>
                            <ProjectSub>Rezervační systém</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>E-voucher.cz</ProjectName>
                            <ProjectSub>Platforma pro voucherové e-shopy</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Zapisdoprvnitridy.cz</ProjectName>
                            <ProjectSub>Aplikace pro zápis do školy</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Tanix.cz</ProjectName>
                            <ProjectSub>Monitoring IoT zařízení</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Crowningarts.com</ProjectName>
                            <ProjectSub>Sociální síť pro umělce</ProjectSub>
                        </ProjectItem>
                        <ProjectItem>
                            <ProjectName>Skautstezery.cz</ProjectName>
                            <ProjectSub>Web skautského oddílu</ProjectSub>
                        </ProjectItem>
                    </ProjectGrid>
                </Section>

                <Section style={{animationDelay: '0.25s'}}>
                    <SectionLabel>Vzdělání</SectionLabel>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>Univerzita Hradec Králové — Fakulta informatiky a managementu</ItemTitle>
                                <ItemDate>09/2022 — 06/2026</ItemDate>
                            </ItemHeader>
                            <ItemRole>Bc. — Aplikovaná informatika</ItemRole>
                            <ItemDesc>Počítačové technologie a systémy.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>VIVES University of Applied Sciences — Kortrijk, Belgie</ItemTitle>
                                <ItemDate>09/2024 — 01/2025</ItemDate>
                            </ItemHeader>
                            <ItemRole>Erasmus+</ItemRole>
                            <ItemDesc>Aplikovaná informatika</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>České vysoké učení technické v Praze</ItemTitle>
                                <ItemDate>08/2021 — 09/2022</ItemDate>
                            </ItemHeader>
                            <ItemDesc>Webové technologie, programování a algoritmizace, databáze.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>

                    <TimelineItem>
                        <TimelineDot />
                        <TimelineContent>
                            <ItemHeader>
                                <ItemTitle>DELTA — Střední škola informatiky a ekonomie, Pardubice</ItemTitle>
                                <ItemDate>09/2017 — 06/2021</ItemDate>
                            </ItemHeader>
                            <ItemRole>Ukončeno maturitou</ItemRole>
                            <ItemDesc>Programování (C#), webové technologie (HTML, CSS, PHP, JS), konfigurace PC, správa databází a sítí, Linux, Adobe.</ItemDesc>
                        </TimelineContent>
                    </TimelineItem>
                </Section>

                <DarkSection style={{animationDelay: '0.3s'}}>
                    <SectionLabelLight>Soutěže & ocenění</SectionLabelLight>
                    <AwardList>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-trophy" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Pardubický talent</AwardTitle>
                                <AwardMeta>Pardubice — Červen 2022</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-award" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ocenění Pardubického kraje</AwardTitle>
                                <AwardMeta>Spolupráce na projektu BurzaŠkol — Duben 2022</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ústřední kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Červenec 2021</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>1.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Krajské kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Duben 2021</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Hackathon KHK 2020</AwardTitle>
                                <AwardMeta>Královéhradecký kraj & tchk.cz — Říjen 2020</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>
                                <i className="fa-solid fa-award" />
                            </AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Mladý řemeslník roku</AwardTitle>
                                <AwardMeta>SSŠČMS, záštita senátu ČR — Říjen 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>4.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Ústřední kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Červen 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                        <AwardItem>
                            <AwardPlace>2.</AwardPlace>
                            <AwardInfo>
                                <AwardTitle>Krajské kolo — programování web</AwardTitle>
                                <AwardMeta>MŠMT — Duben 2019</AwardMeta>
                            </AwardInfo>
                        </AwardItem>
                    </AwardList>
                </DarkSection>

                <FooterSection style={{animationDelay: '0.35s'}}>
                    <LangRow>
                        <LangItem>
                            <LangName>Čeština</LangName>
                            <LangLevel>Rodilý mluvčí</LangLevel>
                        </LangItem>
                        <LangItem>
                            <LangName>Angličtina</LangName>
                        </LangItem>
                        <LangItem>
                            <LangName>Networking Essentials</LangName>
                            <LangLevel>Certifikace</LangLevel>
                        </LangItem>
                    </LangRow>
                </FooterSection>
            </CvCenter>
        </Layout>
    )
}

export default CV

const CvCenter = styled(Center)`
    margin-top: 8em;
`

/* ---- exports used by other components ---- */
export const SectionTitle = styled.h3`
  margin-top: 3em;
  font-size: 1.2em;
  margin-bottom: 0;
  position: relative;
`

export const Paragraph = styled.p`
  margin-top: 0;
  margin-bottom: 2em;
  line-height: 1.8;
`

/* ---- animations ---- */

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/* ---- header ---- */

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 2em;
  margin-bottom: 4em;
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 560px) {
    flex-direction: column;
    text-align: center;
  }
`

const Avatar = styled.div`
  width: 8em;
  height: 8em;
  min-width: 8em;
  background-color: #e9e9e9;
  background-image: url("/img/profile.jpg");
  background-size: cover;
  background-position: center;
  border-radius: 50%;
  border: 3px solid #f0f0f0;
`

const HeaderInfo = styled.div``

const Name = styled.h2`
  font-size: 2em;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
`

const Role = styled.div`
  font-size: 1.05em;
  opacity: 0.45;
  font-weight: 300;
  margin-top: 0.2em;
`

const HeaderMeta = styled.div`
  display: flex;
  gap: 1.5em;
  margin-top: 0.8em;

  @media (max-width: 560px) {
    justify-content: center;
  }
`

const MetaItem = styled.div`
  font-size: 0.82em;
  opacity: 0.4;
  display: flex;
  align-items: center;
  gap: 0.45em;

  i {
    font-size: 0.85em;
  }
`

const MetaLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const Summary = styled.p`
  margin: 0;
  font-size: 0.95em;
  font-weight: 300;
  line-height: 1.8;
  opacity: 0.85;
  max-width: 40em;
`

/* ---- nested roles ---- */

const SubRoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4em;
  margin-top: 1.2em;
  padding-left: 1.1em;
  border-left: 1px solid #ebebeb;
`

const SubRole = styled.div``

const SubRoleTitle = styled.div`
  font-weight: 600;
  font-size: 0.92em;
  letter-spacing: -0.01em;
`

const BulletList = styled.ul`
  font-size: 0.88em;
  opacity: 0.85;
  font-weight: 300;
  line-height: 1.6;
  margin: 0.4em 0 0;
  padding-left: 1.2em;
  max-width: 36em;

  li {
    margin-bottom: 0.1em;
  }
`

const TechLine = styled.div`
  font-size: 0.78em;
  opacity: 0.5;
  font-weight: 300;
  line-height: 1.6;
  margin-top: 0.5em;
  max-width: 36em;
`

/* ---- sections ---- */

const Section = styled.div`
  margin-bottom: 3.5em;
  animation: ${fadeUp} 0.5s ease both;
`

const SectionLabel = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 1.8em;
  padding-bottom: 0.8em;
  border-bottom: 1px solid #ebebeb;
`

/* ---- skills ---- */

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
`

const SkillTag = styled.span`
  font-size: 0.82em;
  font-weight: 400;
  background: #f3f3f3;
  border: 1px solid #eaeaea;
  color: rgba(25, 25, 25, 0.65);
  padding: 0.4em 0.9em;
  border-radius: 2em;
  letter-spacing: 0.01em;
  transition: background 0.2s ease;

  &:hover {
    background: #eaeaea;
  }
`

/* ---- timeline ---- */

const TimelineItem = styled.div`
  display: flex;
  gap: 1.2em;
  margin-bottom: 1.8em;
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 1.4em;
    bottom: -0.8em;
    width: 1px;
    background: #e0e0e0;
  }
`

const TimelineDot = styled.div`
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  background: rgba(25, 25, 25, 0.2);
  margin-top: 0.45em;
`

const TimelineContent = styled.div``

const ItemHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1em;
  flex-wrap: wrap;
`

const ItemTitle = styled.div`
  font-weight: 700;
  font-size: 1em;
  letter-spacing: -0.01em;
`

const ItemDate = styled.div`
  font-size: 0.78em;
  opacity: 0.85;
  font-weight: 400;
`

const ItemRole = styled.div`
  font-size: 0.88em;
  opacity: 0.85;
  margin-top: 0.15em;
`

const ItemDesc = styled.div`
  font-size: 0.88em;
  opacity: 0.85;
  font-weight: 300;
  line-height: 1.6;
  margin-top: 0.25em;
  max-width: 36em;
`

/* ---- project grid ---- */

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14em, 1fr));
  gap: 1em;
`

const ProjectItem = styled.div`
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 0.4em;
  padding: 1.2em 1.3em;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`

const ProjectName = styled.div`
  font-weight: 700;
  font-size: 0.92em;
  margin-bottom: 0.2em;
`

const ProjectSub = styled.div`
  font-size: 0.8em;
  opacity: 0.45;
  font-weight: 300;
  line-height: 1.5;
`

/* ---- dark awards section ---- */

const DarkSection = styled.div`
  background: rgba(25, 25, 25, 1);
  color: #fff;
  border-radius: 0.6em;
  padding: 2.5em 2.5em;
  margin-bottom: 3.5em;
  animation: ${fadeUp} 0.5s ease both;

  @media (max-width: 560px) {
    padding: 2em 1.5em;
  }
`

const SectionLabelLight = styled.div`
  font-size: 0.75em;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.35;
  margin-bottom: 2em;
`

const AwardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2em;
`

const AwardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2em;
`

const AwardPlace = styled.div`
  width: 2.2em;
  min-width: 2.2em;
  height: 2.2em;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.35em;
  font-weight: 800;
  font-size: 0.9em;
  opacity: 0.6;

  i {
    font-size: 0.85em;
  }
`

const AwardInfo = styled.div``

const AwardTitle = styled.div`
  font-weight: 600;
  font-size: 0.92em;
  letter-spacing: -0.01em;
`

const AwardMeta = styled.div`
  font-size: 0.78em;
  opacity: 0.35;
  font-weight: 300;
  margin-top: 0.1em;
`

/* ---- languages footer ---- */

const FooterSection = styled.div`
  margin-bottom: 6em;
  animation: ${fadeUp} 0.5s ease both;
`

const LangRow = styled.div`
  display: flex;
  gap: 3em;
`

const LangItem = styled.div``

const LangName = styled.div`
  font-weight: 700;
  font-size: 0.95em;
`

const LangLevel = styled.div`
  font-size: 0.82em;
  opacity: 0.4;
  font-weight: 300;
  margin-top: 0.15em;
`
